import { GenericItem } from '../types';

export type ComparableItem<TItem> = TItem | null | undefined;

const passthrough = <T>(item: T): T => item;

/**
 * Compares two items for equality using itemToKey when provided, otherwise reference equality.
 *
 * @param item1 - First item to compare
 * @param item2 - Second item to compare
 * @param itemToKey - Optional function to extract a unique key from an item
 * @returns true if items are equal, false otherwise
 */
const areItemsEqual = <TItem extends GenericItem>(
  item1: ComparableItem<TItem>,
  item2: ComparableItem<TItem>,
  itemToKey?: (item: ComparableItem<TItem>) => string | undefined,
): boolean => {
  const toComparable = itemToKey || passthrough<ComparableItem<TItem>>;

  const key1 = toComparable(item1);
  const key2 = toComparable(item2);

  // `x == null` catches both null and undefined values
  // eslint-disable-next-line eqeqeq
  if (key1 == null || key2 == null) return false;

  return key1 === key2;
};

export default areItemsEqual;
